/**
 * @file 红包助力分享页面主组件
 */
import {throttle} from '@lib/utils/descriptor';
import {UgInvokeApp} from '@lib/utils/ugInvokeApp';
import {isOffline} from '@src/lib/constants';
import {Component} from '@src/lib/San';
import {processXbceUrl} from '@src/lib/utils/processImg';
import {getQueryString} from '@src/lib/utils/tools';
import $style from './app.module.less';

export default class App extends Component {
    static template = /* html */ `
        <div class="{{$style.container}}">
            <div class="{{$style.content}}">
                <!-- 背景大图 - 阶梯加载 -->
                <img
                    class="{{$style.bgImage}} {{loadState}}"
                    src="{{currentBackgroundUrl}}"
                    alt="红包活动"
                    on-load="handleImageLoad"
                >
                <!-- 底部按钮：活动结束后隐藏 -->
                <div
                    s-if="{{!isActivityEnded}}"
                    s-ref="invokeBtn"
                    class="{{$style.btn}}"
                    on-click="backToApp"
                    on-touchstart="noop"
                ></div>
            </div>
        </div>
    `;

    initData() {
        return {
            $style,
            sceneName: 'group_redpacket',
            ugInvoke: new UgInvokeApp(),
            // 活动结束时间：2026.03.03 23:59:59
            activityEndTime: new Date('2026-03-03T23:59:59').getTime(),
            // 活动是否已结束
            isActivityEnded: false,
            // 多版本背景图配置
            backgroundVariants: {
                thumbnail: processXbceUrl('https://newapp-prompt.cdn.bcebos.com/bgimg/2026-02/153f30521e247f313849d07fabb39e2f.png', '/format,f_webp/quality,q_30/resize,w_400'),
                standard: processXbceUrl('https://newapp-prompt.cdn.bcebos.com/bgimg/2026-02/153f30521e247f313849d07fabb39e2f.png', '/format,f_webp/quality,q_70/resize,w_750'),
                hd: processXbceUrl('https://newapp-prompt.cdn.bcebos.com/bgimg/2026-02/153f30521e247f313849d07fabb39e2f.png', '/format,f_webp/quality,q_100'),
            },
            // 活动结束后的多版本背景图配置
            endedBackgroundVariants: {
                thumbnail: processXbceUrl('https://newapp-prompt.cdn.bcebos.com/bgimg/2026-02/7fc9413f879a89cadcbeca63b8fac8f5.png', '/format,f_webp/quality,q_30/resize,w_400'),
                standard: processXbceUrl('https://newapp-prompt.cdn.bcebos.com/bgimg/2026-02/7fc9413f879a89cadcbeca63b8fac8f5.png', '/format,f_webp/quality,q_70/resize,w_750'),
                hd: processXbceUrl('https://newapp-prompt.cdn.bcebos.com/bgimg/2026-02/7fc9413f879a89cadcbeca63b8fac8f5.png', '/format,f_webp/quality,q_100'),
            },
            currentBackgroundUrl: '',
            loadState: 'initial',
            preloadedImages: new Map<string, HTMLImageElement>(),
        };
    }

    created() {
        this.checkActivityStatus();
        this.getInvokeInfo();
        this.preloadImages();
    }

    attached() {
        // 微信环境下渲染开放标签
        this.renderWxTag();
    }

    /**
     * 检查活动状态
     * 通过URL参数expired或时间判断活动是否到期
     */
    checkActivityStatus() {
        const expired = getQueryString('expired');
        const activityEndTime = this.data.get('activityEndTime') as number;
        const now = Date.now();

        // URL参数标记过期或时间过期 -> 更换背景图并隐藏按钮
        if (expired === '1' || now > activityEndTime) {
            this.data.set('isActivityEnded', true);
        }
    }

    /**
     * 获取回流配置
     */
    getInvokeInfo() {
        const ugInvoke = this.data.get('ugInvoke');
        const sceneName = this.data.get('sceneName');
        const uk = getQueryString('uk');
        const {protocol, host} = window.location;
        const targetUrl = isOffline ? `${protocol}//${host}/app/myasset` : 'https://yiyanapp.baidu.com/app/myasset';
        const backUrl = uk
            ? `${targetUrl}?uk=${uk}`
            : targetUrl;
        const schema = `baiduwanhua://newbrowse/detail?params=${encodeURIComponent(JSON.stringify({
            url: backUrl,
        }))}`;
        ugInvoke?.getInvokeInfo({
            [sceneName]: {
                params: schema,
            },
        });
    }

    /**
     * 微信环境渲染开放标签
     */
    renderWxTag() {
        const isActivityEnded = this.data.get('isActivityEnded');
        if (isActivityEnded) {
            return;
        }
        const ugInvoke = this.data.get('ugInvoke');
        const sceneName = this.data.get('sceneName');
        const invokeBtn = this.ref('invokeBtn') as unknown as HTMLElement;
        if (invokeBtn) {
            ugInvoke?.renderWxTag?.({
                wxConf: {
                    wxDom: invokeBtn,
                    onClick: () => {},
                },
                sceneName,
            });
        }
    }

    /**
     * 空函数，用于激活iOS的:active状态
     */
    noop() {}

    /**
     * 点击按钮回流APP
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    @throttle(500)
    backToApp() {
        const sceneName = this.data.get('sceneName');
        const ugInvoke = this.data.get('ugInvoke');
        ugInvoke?.execInvoke(sceneName);
    }

    /**
     * 获取当前使用的背景图配置
     */
    getCurrentVariants() {
        const isActivityEnded = this.data.get('isActivityEnded');
        return isActivityEnded
            ? this.data.get('endedBackgroundVariants')
            : this.data.get('backgroundVariants');
    }

    /**
     * 阶梯形加载图片
     * 先加载缩略图，再并行预加载标准图和高清图，按顺序显示
     */
    preloadImages() {
        const variants = this.getCurrentVariants();

        // 设置初始图片为缩略图
        this.data.set('currentBackgroundUrl', variants.thumbnail);
        this.data.set('loadState', 'loading-thumbnail');

        // 并行预加载标准图和高清图
        this.preloadImage(variants.standard, 'standard');
        this.preloadImage(variants.hd, 'hd');
    }

    /**
     * 预加载单张图片
     */
    preloadImage(url: string, key: string) {
        const preloadedImages = this.data.get('preloadedImages');
        const img = new Image();

        img.onload = () => {
            preloadedImages.set(key, img);
            this.tryUpgradeImage();
        };

        img.src = url;
    }

    /**
     * 尝试升级到更高质量的图片
     */
    tryUpgradeImage() {
        const currentState = this.data.get('loadState');
        const preloadedImages = this.data.get('preloadedImages') as Map<string, HTMLImageElement>;
        const variants = this.getCurrentVariants();

        // 缩略图已加载 -> 升级到标准图
        if (currentState === 'loaded-thumbnail' && preloadedImages.has('standard')) {
            this.data.set('currentBackgroundUrl', variants.standard);
            this.data.set('loadState', 'loaded-standard');
            this.tryUpgradeImage(); // 递归检查是否可以继续升级
        }
        // 标准图已加载 -> 升级到高清图
        else if (currentState === 'loaded-standard' && preloadedImages.has('hd')) {
            this.data.set('currentBackgroundUrl', variants.hd);
            this.data.set('loadState', 'loaded-hd');
        }
    }

    /**
     * 图片加载完成处理
     */
    handleImageLoad() {
        const currentState = this.data.get('loadState');

        if (currentState === 'loading-thumbnail') {
            this.data.set('loadState', 'loaded-thumbnail');
            this.tryUpgradeImage();
        }
        else if (currentState === 'loading-standard') {
            this.data.set('loadState', 'loaded-standard');
        }
        else if (currentState === 'loading-hd') {
            this.data.set('loadState', 'loaded-hd');
        }
    }
}
